// Slice-plane intent handlers (XY/ZY/ZX position, visibility, opacity, pixel
// filter, and the transparent-bounds toggle which fans out across slices,
// volume, and colormap).

import { INTENTS } from '@store/intents';
import { params } from '@store/instance';
import {
    updateSlicePosition,
    applyVisibility as slApplyVisibility,
    applyDisplaySettings as slApplyDisplaySettings,
    applyTransparentBounds as slApplyTransparentBounds,
    applyTextureFilters as slApplyTextureFilters,
} from '@scene/slices';
import { applyTransparentBounds as volApplyTransparentBounds } from '@scene/volume';
import { syncColormap } from '@app/colormapSync';
import { updateBindingFields } from '@store/bindings';
import type { ColormapBindingId } from '@store/schema';
import type { Store } from '@store/store';
import type { State } from '@store/schema';
import type { ViewerContext } from '../context';

/** Bindings touched by any primitive — mirror transparentBounds onto each. */
function activeBindings(): ColormapBindingId[] {
    const ids = new Set<ColormapBindingId>();
    for (const k of ['slicesColormapId', 'volumeColormapId', 'contourColormapId', 'fieldlinesColormapId'] as const) {
        const v = params[k];
        if (v) ids.add(v);
    }
    return [...ids];
}

export function register(store: Store<State>, viewer: ViewerContext): Array<() => void> {
    const offs: Array<() => void> = [];

    offs.push(store.onIntent(INTENTS.SET_SLICE_POSITION, (p) => {
        if (!p || typeof p.value !== 'number') return;
        if (p.axis === 'XY') params.sliceZ = p.value;
        else if (p.axis === 'ZY') params.sliceZY = p.value;
        else if (p.axis === 'ZX') params.sliceZX = p.value;
        else return;
        updateSlicePosition(p.axis, viewer, params, p.value);
    }));

    offs.push(store.onIntent(INTENTS.SET_SLICE_VISIBILITY, (p) => {
        if (!p) return;
        const v = !!p.visible;
        if (p.axis === 'XY') params.showFieldSlice = v;
        else if (p.axis === 'ZY') params.showFieldSliceZY = v;
        else if (p.axis === 'ZX') params.showFieldSliceZX = v;
        slApplyVisibility(viewer, params);
    }));

    offs.push(store.onIntent(INTENTS.SET_SLICE_OPACITY, (p) => {
        if (!p || typeof p.value !== 'number') return;
        params.sliceOpacity = p.value;
        slApplyDisplaySettings(viewer, params);
    }));

    offs.push(store.onIntent(INTENTS.SET_SLICE_PIXELATED, (p) => {
        if (!p) return;
        params.slicePixelated = !!p.value;
        slApplyTextureFilters(viewer, params);
    }));

    offs.push(store.onIntent(INTENTS.SET_TRANSPARENT_BOUNDS, (p) => {
        if (!p) return;
        const v = !!p.value;
        params.transparentBounds = v;
        // Mirror onto every active binding so the colormapSync fan-out
        // (which reads from bindings) picks up the new transparent flag.
        for (const id of activeBindings()) updateBindingFields(id, { transparentBounds: v });
        // Force slice opacity off-1.0 when the toggle is on; fully opaque
        // slice materials skip alpha entirely and render out-of-range pixels
        // as black instead of transparent. Restore on toggle-off.
        const curr = params.sliceOpacity;
        if (v && (curr >= 1.0 || !isFinite(curr))) {
            params.sliceOpacity = 0.99;
        } else if (!v && Math.abs(curr - 0.99) < 1e-6) {
            params.sliceOpacity = 1.0;
        }
        slApplyDisplaySettings(viewer, params);
        slApplyTransparentBounds(viewer, params);
        volApplyTransparentBounds(viewer, params, v);
        syncColormap(viewer, params);
        store.dispatch(INTENTS.UI_PANE_REFRESH);
    }));

    return offs;
}
